import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ChartContainer } from '@/components/ui/chart';
import type { GenreSpeedData } from '@/lib/analyticsUtils';

interface GenreSpeedComparisonProps {
  genreData: GenreSpeedData[];
}

export function GenreSpeedComparison({ genreData }: GenreSpeedComparisonProps) {
  if (genreData.length === 0) return null;

  const chartConfig = {
    averageDays: {
      label: "Average Days",
      color: "hsl(var(--primary))",
    },
  };

  return (
    <Card className="card-elevated">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          Reading Speed by Genre
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-6">
          Average days to finish books in different genres
        </p>
        
        <ChartContainer config={chartConfig} className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={genreData} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
              <YAxis 
                type="category" 
                dataKey="genre" 
                stroke="hsl(var(--muted-foreground))"
                width={120}
                tick={{ fontSize: 12 }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
                labelStyle={{ color: 'hsl(var(--popover-foreground))' }}
                formatter={(value: number, name: string, props: any) => [
                  `${value} days (${props.payload.bookCount} books)`,
                  'Average Reading Time'
                ]}
              />
              <Bar 
                dataKey="averageDays" 
                fill="hsl(var(--primary))" 
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>

        <div className="mt-4 text-xs text-muted-foreground text-center">
          Showing genres with 2+ books read
        </div>
      </CardContent>
    </Card>
  );
}
